import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { DrawOutlined } from '@mui/icons-material';
import { Button, Card, DatePicker, Form, message, Modal, Radio, Select, Spin, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { ApiHandler } from '../services/apiHandler';
import ConfirmAlert from './confirmAlert';

interface CreateWorkOrderProps {
  cancelWorkOrderCreation: () => void;
  setPreloadConfirmVisible: (visible: boolean) => void;
  isPlacingWorkOrder: boolean;
  setIsPlacingWorkOrder: (value: boolean) => void;
  preloadConfirmVisible: boolean;
  totalServiceDrops: any[];
  setTotalServiceDrops: (value: any[]) => void;
  setRFImageUrl: (value: string) => void;
  setRFBounds: (value: any) => void;
  isEmergency: boolean;
  setIsEmergency: (value: boolean) => void;
  getAssetsInBounds: () => void;
  venuesOnMap: any[];
  buildingsOnMap: any[];
  position: { lat: number; lng: number };
  setWorkOrderVenueId: (id: any) => void;
  setWorkOrderBuildingId: (id: any) => void;
  setDrawMode: (mode: any) => void;
  drawMode: any;
  cancelEmergencyDrawing: () => void;
  emergencyBoundaryPolygon: any;
}
const CreateWorkOrder: React.FC<CreateWorkOrderProps> = (props) => {
  const [rfSimulations, setRfSimulations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [selectedRfSimulation, setSelectedRfSimulation] = useState<number | null>(null);

  const [confirmWorkOrderCreation, setConfirmWorkOrderCreation] = useState(false);

  const [associatedVenue, setAssociatedVenue] = useState<any>(null);
  const [associatedBuilding, setAssociatedBuilding] = useState<any>(null);

  const [assetOnVenueAlert, setAssetOnVenueAlert] = useState(false);
  const [assetOnBuildingAlert, setAssetOnBuildingAlert] = useState(false);

  useEffect(() => {
    getAllSimulations();
  }, []);

  useEffect(() => {
    if (selectedRfSimulation) {
      props.setRFImageUrl(rfSimulations.find((simulation: any) => simulation.id === selectedRfSimulation)?.rf_image_url);
      props.setRFBounds(rfSimulations.find((simulation: any) => simulation.id === selectedRfSimulation)?.rf_bounds);
    }
  }, [selectedRfSimulation]);

  const getAllSimulations = () => {
    setIsLoading(true);
    ApiHandler({ apiName: 'getRFSimulations', params: {}, body: {} })
      .then((response: any) => {
        setRfSimulations(response.data.simulations);
      })
      .catch((error: any) => {
        message.error(error?.data?.message ?? error?.message ?? 'Failed to get RF simulations');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (props.isPlacingWorkOrder) {
      validateIsWorkOrderOnBuilding();
    }
  }, [props.position]);

  useEffect(() => {
    if (associatedVenue) {
      setAssetOnVenueAlert(true);
    }
  }, [associatedVenue]);

  useEffect(() => {
    if (associatedBuilding) {
      setAssetOnBuildingAlert(true);
    }
  }, [associatedBuilding]);

  const validateIsWorkOrderOnVenue = () => {
    let contains: boolean = false;
    let tempVenue: any = null;
    for (let venue of props.venuesOnMap) {
      let doesContain: boolean = window.google.maps.geometry.poly.containsLocation(
        new window.google.maps.LatLng(props.position.lat, props.position.lng),
        new window.google.maps.Polygon({ paths: venue?.polygon })
      );
      if (doesContain) {
        contains = doesContain;
        tempVenue = venue;
      }
    }

    if (contains) {
      setAssociatedVenue(tempVenue);
      return true;
    }
  };

  const validateIsWorkOrderOnBuilding = () => {
    let contains: boolean = false;
    let tempBuilding: any = null;
    for (let building of props.buildingsOnMap) {
      let doesContain: boolean = window.google.maps.geometry.poly.containsLocation(
        new window.google.maps.LatLng(props.position.lat, props.position.lng),
        new window.google.maps.Polygon({ paths: building?.polygon_points })
      );
      if (doesContain) {
        contains = doesContain;
        tempBuilding = building;
      }
    }
    if (contains) {
      setAssociatedBuilding(tempBuilding);
      return true;
    } else {
      validateIsWorkOrderOnVenue();
    }
  };

  const createWorkOrder = () => {
    props.cancelWorkOrderCreation();
  };

  const deleteServiceDrop = (trackingId: string) => {
    setIsDeleteLoading(true);
    ApiHandler({ apiName: 'deleteAsset', params: { tracking_id: trackingId }, body: {} })
      .then((response: any) => {
        props.setTotalServiceDrops(props.totalServiceDrops.filter((serviceDrop: any) => serviceDrop.tracking_id !== trackingId));
        props.getAssetsInBounds();
        message.success('Service drop deleted successfully');
      })
      .catch((error: any) => {
        message.error(error?.data?.message ?? error?.message ?? 'Failed to delete service drop');
      })
      .finally(() => {
        setIsDeleteLoading(false);
      });
  };

  const setBoundaryPolygon = () => {
    // set boundary plot from google.maps.LatLng to {lat: number, lng: number}
    let boundary = props.emergencyBoundaryPolygon?.map((point: any) => {
      return {
        lat: parseFloat(point.lat().toFixed(6)),
        lng: parseFloat(point.lng().toFixed(6)),
      };
    });

    // push first vertex as last vertex to make it a closed polygon
    boundary?.push(boundary[0]);
    return boundary;
  };

  return (
    <>
      <div className="position-absolute d-flex flex-column gap-2 overflow-auto" style={{ top: '16px', left: '16px', bottom: '16px', width: '400px' }}>
        <Card
          actions={[
            <Button disabled={props.totalServiceDrops.length === 0} type="primary" key="confirm" onClick={() => setConfirmWorkOrderCreation(true)}>
              Confirm Work Order
            </Button>,
            // <Button type="primary" key="place-service-drop" onClick={() => props.setPreloadConfirmVisible(true)}>
            //   Add Service Drop
            // </Button>,
          ]}
          style={props.isEmergency ? { borderColor: 'red' } : {}}
          title={
            <div className="d-flex flex-row justify-content-between pt-2">
              <div className="d-flex flex-row align-items-center gap-3">
                <Typography.Title level={4}>Work Order</Typography.Title>
                <Spin size="small" spinning={isLoading} />
              </div>
              <Button type="link" color="danger" style={{ color: 'red' }} onClick={() => props.cancelWorkOrderCreation()}>
                Cancel
              </Button>
            </div>
          }
          id="work-order-creation-card">
          <Form autoComplete="off" labelAlign="left" className="overflow-auto" labelCol={{ span: 10 }} wrapperCol={{ span: 24 }}>
            <Form.Item label="Work Order Type" name="work_order_type">
              <Radio.Group
                value={props.isEmergency ? 'emergency' : 'service_drop'}
                defaultValue={props.isEmergency ? 'emergency' : 'service_drop'}
                onChange={(e) => {
                  props.setIsEmergency(e.target.value === 'emergency');
                }}>
                <Radio value="service_drop">Install</Radio>
                <Radio value="emergency">Emergency</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item label="RF Simulation" name="rf_simulation">
              <Select
                loading={isLoading}
                options={rfSimulations.map((simulation: any) => ({ label: simulation.simulation_name, value: simulation.id }))}
                placeholder="Select RF Simulation"
                size="small"
                className="w-100"
                onChange={(value: any) => {
                  setSelectedRfSimulation(value);
                }}
              />
            </Form.Item>
            {props.isEmergency && (
              <>
                <Form.Item label="Start Date" name="start_date" className="mb-0">
                  <DatePicker
                    size="small"
                    className="w-100"
                    onChange={(value: any) => {
                      // setStartDate(value);
                    }}
                  />
                </Form.Item>
                <Form.Item label="End Date" name="end_date">
                  <DatePicker
                    size="small"
                    className="w-100"
                    onChange={(value: any) => {
                      // setEndDate(value);
                    }}
                  />
                </Form.Item>
              </>
            )}
          </Form>
          {props.isEmergency && props.drawMode === 'draw_emergency' ? (
            <div className="d-flex flex-row gap-2 mb-3">
              <Button
                type="default"
                className="w-100"
                style={{ color: 'red' }}
                icon={<DeleteOutlined style={{ color: 'red', fontSize: '16px' }} />}
                onClick={() => {
                  props.cancelEmergencyDrawing();
                }}>
                Delete
              </Button>
              <Button
                type="default"
                className="w-100"
                style={{ color: '#0059b2' }}
                icon={<DrawOutlined style={{ color: '#0059b2', fontSize: '16px' }} />}
                onClick={() => {
                  props.setDrawMode(null);
                }}>
                Confirm
              </Button>
            </div>
          ) : props.isEmergency ? (
            <Button
              type="default"
              className="w-100 mb-3"
              style={{ color: '#0059b2' }}
              icon={<DrawOutlined style={{ color: '#0059b2', fontSize: '16px' }} />}
              onClick={() => {
                props.setDrawMode('draw_emergency');
              }}>
              Draw Affected Area
            </Button>
          ) : null}
          <div className="d-flex flex-row align-items-center gap-2">
            <Typography.Text strong>{`Service Drops (${props.totalServiceDrops.length})`}</Typography.Text>
            <Button
              size="small"
              type="default"
              className="d-flex justify-content-center align-items-center"
              onClick={() => {
                validateIsWorkOrderOnBuilding();
                props.setIsPlacingWorkOrder(true);
              }}>
              <PlusOutlined />
            </Button>
          </div>
          {props.totalServiceDrops.map((serviceDrop: any) => (
            <Card
              size="small"
              key={serviceDrop.asset_id}
              className="mt-3"
              title={
                <div className="d-flex flex-row justify-content-between align-items-center">
                  <Typography.Text strong>{`ID: ${serviceDrop?.tracking_id ?? 'N/A'}`}</Typography.Text>
                  {isDeleteLoading ? (
                    <Spin size="small" spinning={isDeleteLoading} />
                  ) : (
                    <DeleteOutlined
                      color="danger"
                      style={{ color: 'red' }}
                      onClick={() => {
                        deleteServiceDrop(serviceDrop?.tracking_id);
                      }}
                    />
                  )}
                </div>
              }>
              <Typography.Text type="secondary">{serviceDrop?.address}</Typography.Text>
            </Card>
            // <div
            //   key={serviceDrop.asset_id}
            //   style={{ border: '1px solid #e0e0e0', padding: '8px', borderRadius: '4px', marginTop: '8px' }}
            //   className="d-flex flex-row justify-content-between align-items-center gap-2">

            //   <Button type="link" color="danger" style={{ color: 'red' }} onClick={() => {}}>
            //     <DeleteOutlined />
            //   </Button>
            // </div>
          ))}
        </Card>

        {/* <Modal
        open={props.preloadConfirmVisible}
        title="Add Service Drop"
        okText="Add"
        cancelText="Cancel"
        onCancel={() => props.setPreloadConfirmVisible(false)}
        onOk={addServiceDrop}>
        <div className="d-flex flex-column gap-2 my-4">
          <Typography.Text>Do you confirm this location for service drop?</Typography.Text>
          <Typography.Text type="secondary">The field technician will arrive at this location to install assets.</Typography.Text>
        </div>
      </Modal> */}

        <Modal
          open={confirmWorkOrderCreation}
          title="Confirm Work Order Creation"
          okText="Confirm"
          cancelText="Cancel"
          okButtonProps={{ disabled: props.totalServiceDrops.length === 0 }}
          onCancel={() => setConfirmWorkOrderCreation(false)}
          onOk={createWorkOrder}>
          <div className="d-flex flex-column gap-2 my-4">
            {props.totalServiceDrops.length > 0 && <Typography.Text>Are you sure you want to finalize the work order?</Typography.Text>}
            {props.totalServiceDrops.length === 0 && (
              <>
                <Typography.Text strong type="danger">
                  No service drops added yet.
                </Typography.Text>
                <Typography.Text>Add atleast one service drop.</Typography.Text>
              </>
            )}
            {props.totalServiceDrops.length > 0 && (
              <Typography.Text strong type="success">
                You have added {props.totalServiceDrops.length} service drop(s).
              </Typography.Text>
            )}
          </div>
        </Modal>
      </div>
      <ConfirmAlert
        message={`You have moved the location pin in the ${associatedVenue?.name} venue.`}
        note="Work Order will be associated to this venue."
        primaryText={'OK'}
        secondaryText={null}
        secondaryAction={() => {
          setAssetOnVenueAlert(false);
          setAssociatedVenue(null);
        }}
        visible={assetOnVenueAlert}
        title={`Location landed in venue `}
        primaryAction={() => {
          props.setWorkOrderVenueId(associatedVenue?.id);
          setAssetOnVenueAlert(false);
        }}
        onClose={() => {
          props.setIsPlacingWorkOrder(false);
          setAssetOnVenueAlert(false);
          setAssociatedVenue(null);
        }}
      />

      <ConfirmAlert
        message={`You have moved the location pin in the ${associatedBuilding?.name} building.`}
        note="Work Order will be associated to this building."
        primaryText={'OK'}
        secondaryText={null}
        secondaryAction={() => {
          setAssetOnBuildingAlert(false);
        }}
        visible={assetOnBuildingAlert}
        title={`Location landed in building `}
        primaryAction={() => {
          props.setWorkOrderVenueId(associatedBuilding?.id);
          setAssetOnBuildingAlert(false);
        }}
        onClose={() => {
          setAssetOnBuildingAlert(false);
          props.setIsPlacingWorkOrder(false);
          setAssociatedBuilding(null);
        }}
      />
    </>
  );
};

export default CreateWorkOrder;
